// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Landmark, Plus } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Select, Pagination, DataTable } from '@/components/ui'
import type { Column } from '@/components/ui/DataTable'
import { useFixedAssets } from '@/hooks/useAccounting'
import { useProperties } from '@/hooks/useProperties'
import { useSelectedPropertyStore } from '@/stores'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { FixedAsset } from '@/types'
import { formatBaht } from '@/lib/money'

type StatusFilter = '' | 'active' | 'fully_depreciated' | 'disposed'

export function FixedAssetListPage() {
  const { t } = useTranslation('accounting')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const formatDate = useDateFormatter()

  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<StatusFilter>('')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)

  const { data, isLoading, isFetching, error } = useFixedAssets(page, {
    status: status || undefined,
    property_id: selectedPropertyId ?? undefined,
  })

  const { data: propertiesData } = useProperties()
  const properties = propertiesData?.data || []

  const assets = data?.data || []

  const propertyOptions = [
    { value: '', label: t('filters.all_properties') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const statusOptions = [
    { value: '', label: t('filters.all_statuses') },
    { value: 'active', label: t('asset_statuses.active') },
    { value: 'fully_depreciated', label: t('asset_statuses.fully_depreciated') },
    { value: 'disposed', label: t('asset_statuses.disposed') },
  ]

  const getStatusBadge = (asset: FixedAsset) => {
    switch (asset.status) {
      case 'active':
        return <Badge variant="success">{t('asset_statuses.active')}</Badge>
      case 'fully_depreciated':
        return <Badge variant="warning">{t('asset_statuses.fully_depreciated')}</Badge>
      case 'disposed':
        return <Badge variant="ghost">{t('asset_statuses.disposed')}</Badge>
    }
  }

  const columns: Column<FixedAsset>[] = [
    {
      key: 'name',
      header: t('labels.name'),
      render: (a) => <span className="font-medium">{a.name}</span>,
    },
    {
      key: 'acquisition_date',
      header: t('labels.acquisition_date'),
      render: (a) => formatDate(a.acquisition_date),
      hideBelow: 'md',
    },
    {
      key: 'acquisition_cost',
      header: t('labels.acquisition_cost'),
      render: (a) => (
        <span className="tabular-nums">{formatBaht(a.acquisition_cost, { locale: 'en', decimals: 2 })}</span>
      ),
    },
    {
      key: 'accumulated_depreciation',
      header: t('labels.accumulated_depreciation'),
      render: (a) => (
        <span className="tabular-nums">{formatBaht(a.accumulated_depreciation, { locale: 'en', decimals: 2 })}</span>
      ),
      hideBelow: 'lg',
    },
    {
      key: 'net_book_value',
      header: t('labels.net_book_value'),
      render: (a) => (
        <span className="tabular-nums font-medium">{formatBaht(a.net_book_value, { locale: 'en', decimals: 2 })}</span>
      ),
      hideBelow: 'lg',
    },
    {
      key: 'depreciation_method',
      header: t('labels.depreciation_method'),
      render: (a) => t(`depreciation_methods.${a.depreciation_method}`),
      hideBelow: 'md',
    },
    {
      key: 'status',
      header: t('labels.status'),
      render: (a) => getStatusBadge(a),
    },
  ]

  const activeAssetFilterCount = [status, selectedPropertyId].filter(Boolean).length

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('fixed_assets.title') }]} />

      <PageHeader
        title={t('fixed_assets.title')}
        description={t('fixed_assets.description')}
        isRefreshing={isFetching && !isLoading}
        // Property scope top-right (TC-MUI-A5 titleAction, mobile-only; desktop
        // picker stays in the filter card) — consistent across accounting pages.
        titleAction={
          <Select
            data-testid="page-property-picker-mobile"
            aria-label={tc('labels.select_property')}
            size="sm"
            value={selectedPropertyId ? String(selectedPropertyId) : ''}
            onChange={(e) => {
              setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)
              setPage(1)
            }}
            options={propertyOptions}
          />
        }
        primaryAction={{
          key: 'new',
          label: t('fixed_assets.new_asset'),
          icon: Plus,
          to: '/accounting/fixed-assets/new',
        }}
      />

      {/* Filters */}
      <Card>
        {/* Mobile-only toggle */}
        <button
          type="button"
          className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium"
          aria-expanded={mobileFiltersOpen}
          aria-controls="asset-filters-body"
          onClick={() => setMobileFiltersOpen((v) => !v)}
        >
          <span>
            {tc('filters.toggle')}
            {activeAssetFilterCount > 0 && (
              <Badge variant="count" size="sm" className="ml-2">{activeAssetFilterCount}</Badge>
            )}
          </span>
          <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
        </button>

        <div
          id="asset-filters-body"
          className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
        >
          <Select
            label={t('labels.status')}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as StatusFilter)
              setPage(1)
            }}
            options={statusOptions}
            className="w-full sm:w-48 min-w-0"
          />
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            title={t('messages.error_loading')}
            description={t('messages.error_description')}
            action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
          />
        </Card>
      ) : assets.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Landmark className="w-12 h-12" />}
            title={t('fixed_assets.empty_title')}
            description={t('fixed_assets.empty_description')}
            action={
              <Button
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => navigate('/accounting/fixed-assets/new')}
              >
                {t('fixed_assets.new_asset')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            data={assets}
            keyExtractor={(a) => a.id}
            onRowClick={(a) => navigate(`/accounting/fixed-assets/${a.id}`)}
          />
        </Card>
      )}

      {data?.meta && data.meta.total_count > 0 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            totalItems={data.meta.total_count}
            pageSize={data.meta.per_page}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  )
}
